import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import TableEditorCell from './TableEditorCell';
import TableRowDecoratorsCell from '../fields/table/TableRowDecoratorsCell';
import Icon from '../../misc/Icon';

import FieldModes from '../../../constants/FieldModes';
import FieldTypes from '../../../constants/FieldTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldTableCustomColumnIds from '../../../constants/FieldTableCustomColumnIds';
import { mergeRowDecorators } from '../../../lib/decoratorHelper';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
`;

const StyledCell = styled.div`
  box-sizing: border-box;
  max-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  min-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  padding-right: 2px;
  padding-left: 2px;
`;

const StyledNumberCell = styled.div`
  color: ${(props) => props.theme.fontColorPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  max-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  min-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  padding: 3px;
  font-size: 12px;
`;

const StyledDecoratorEditorCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  overflow: hidden;
`;

const StyledIconOnlyDecoratorCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  max-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  min-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  padding: 3px 0;
`;

class TableEditorRow extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {
    activeFieldPosition: PropTypes.object,
    id: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    columns: PropTypes.array,
    rowDecorators: PropTypes.array,
    focused: PropTypes.string,
    disableDragAndDrop: PropTypes.bool,
    onDelete: PropTypes.func,
  }


  render() {

    const {
      id,
      activeFieldPosition,
      row,
      rowIndex,
      columns,
      rowDecorators,
      focused,
      disableDragAndDrop,
      onDelete
    } = this.props;

    const { dangerColor } = this.context;

    const cells = columns.map((column) => {

      const rowId = row._id;
      const columnId = column._id;

      const isUtilityColumn = (
        column._id === FieldTableCustomColumnIds.rowMenu ||
        column._id === FieldTableCustomColumnIds.rowNumber ||
        column._id === FieldTableCustomColumnIds.rowDecorators
      );

      let columnWidth;
      if (column._id === FieldTableCustomColumnIds.rowDecorators) {
        columnWidth = column.width ?? 80;
      } else if (isUtilityColumn) {
        columnWidth = 24;
      } else {
        columnWidth = 200;
      }

      const cellKey = `${rowId}_${rowIndex}_${columnId}`;
      const isFocused = focused === `${rowIndex}-${columnId}`;

      if (column._id === FieldTableCustomColumnIds.rowMenu) {
        return (
          <StyledNumberCell
            key={cellKey}
            width={columnWidth}
          >
            {!onDelete ? null : (
              <Icon
                id={`${rowId}-delete-icon`}
                icon={ThemeIcons.settings.delete}
                color={dangerColor}
                onClick={() => onDelete(rowId, rowIndex)}
              />
            )}
          </StyledNumberCell>
        );
      } else if (column._id === FieldTableCustomColumnIds.rowNumber) {
        return (
          <StyledNumberCell
            theme={this.context}
            key={cellKey}
            width={columnWidth}
          >
            {rowIndex + 1}
          </StyledNumberCell>
        );
      } else if (column._id === FieldTableCustomColumnIds.rowDecorators) {

        const mergedDecorators = mergeRowDecorators(rowDecorators, row.decorators?.all);
        const validDecorators = mergedDecorators ? mergedDecorators.filter((d) => !d.deleted && (d.icon || d.label)) : [];
        const hasMultiple = validDecorators.length > 1;

        if (hasMultiple) {
          return (
            <StyledCell key={cellKey} width={columnWidth}>
              <StyledDecoratorEditorCell theme={this.context}>
                <TableRowDecoratorsCell
                  decorators={validDecorators}
                  fieldMode={FieldModes.edit}
                  borderWidth={0}
                  borderColor="transparent"
                />
              </StyledDecoratorEditorCell>
            </StyledCell>
          );
        }

        return (
          <StyledIconOnlyDecoratorCell key={cellKey} width={columnWidth}>
            <TableRowDecoratorsCell
              decorators={validDecorators}
              fieldMode={FieldModes.edit}
              borderWidth={0}
              borderColor="transparent"
            />
          </StyledIconOnlyDecoratorCell>
        );

      } else if (column.type === FieldTableColumnTypes.dropdown) {

        const targetValueOption = column.options.find((option) => !option.deleted);

        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.dropdown}
              targetValue={targetValueOption ? targetValueOption._id : null}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );

      } else if (column.type === FieldTableColumnTypes.multiSelect) {

        const targetValueOption = column.options && column.options.length > 0 ? column.options.find((option) => !option.deleted):'';

        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.multiSelect}
              targetValue={targetValueOption ? targetValueOption._id : null}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );

      } else if (column.type === FieldTableColumnTypes.image) {

        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.image}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );

      }
      else if (column.type === FieldTableColumnTypes.number) {
        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.number}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );
      } else if (column.type === FieldTableColumnTypes.date) {
        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.date}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );
      } else if (column.type === FieldTableColumnTypes.barcode) {
        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.barcode}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );
      }
      else if (column.type === FieldTableColumnTypes.signature) {
        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.signature}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );
      }
      else {
        return (
          <StyledCell key={cellKey} width={columnWidth}>
            <TableEditorCell
              id={id}
              activeFieldPosition={activeFieldPosition}
              type={FieldTypes.table}
              displayType={FieldDisplayTypes.text}
              rowId={rowId}
              rowIndex={rowIndex}
              columnId={columnId}
              columnType={FieldTableColumnTypes.text}
              focused={isFocused}
              width={columnWidth}
              disableDragAndDrop={disableDragAndDrop}
            />
          </StyledCell>
        );

      }

    });

    return (
      <StyledRow
        key={row._id}
        data-testid={`${id}-${row._id}-${rowIndex}-table-editor-row`}
      >
        {cells}
      </StyledRow>
    );

  }

}

export default TableEditorRow;
